import { useId, useState } from 'react';
import type { AggregatesArtifact, GeoMultiPolygonCoordinates, KansaiMapArtifact } from '../../data/types';
import { CURATED_STORIES } from '../../data/curatedStories';
import { cn } from '../../lib/cn';
import { ChartFrame, DataLabel } from '../../viz/primitives';

type Story = AggregatesArtifact['story'];

function MetricRow({ items }: { items: Array<{ value: string; label: string }> }) {
  return (
    <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-atc-navy/10 bg-atc-navy/10 md:grid-cols-3">
      {items.map((item) => (
        <div key={item.label} className="bg-white/90 p-4 md:p-5">
          <dt className="text-xs font-bold text-atc-slate">{item.label}</dt>
          <dd className="mt-2 text-2xl font-bold tabular-nums text-atc-navy">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function ChapterOneVisuals({ story, beat }: { story: Story; beat: 0 | 1 }) {
  const h = story.headlines;
  if (beat === 0) {
    return (
      <ChartFrame title="来場者の輪郭" description="同行者、来場目的、来館頻度を同じ1,386人から読む。">
        <MetricRow items={[
          { label: '家族', value: `${h.family.n}人・${h.family.rate}%` },
          { label: 'イベント', value: `${h.eventPurpose.n}人・${h.eventPurpose.rate}%` },
          { label: '初回＋年1回', value: `${h.lightVisitors.n}人・${h.lightVisitors.rate}%` },
          { label: '女性', value: `${h.female.n}人・${h.female.rate}%` },
          { label: '電車', value: `${h.train.n}人・${h.train.rate}%` },
        ]} />
      </ChartFrame>
    );
  }

  const stages = ['awareness', 'purpose', 'satisfaction'] as const;
  const sankeyPositions = new Map<string, { x: number; y: number }>();
  stages.forEach((stage, stageIndex) => {
    const nodes = story.sankey.nodes.filter((node) => node.stage === stage);
    nodes.forEach((node, nodeIndex) => {
      sankeyPositions.set(node.id, {
        x: 70 + stageIndex * 330,
        y: 44 + nodeIndex * (300 / Math.max(nodes.length - 1, 1)),
      });
    });
  });
  return (
    <ChartFrame
      title="認知から満足まで"
      description={story.sankey.note}
      aria-label="認知経路、来場目的、満足度の3段階集計"
    >
      <svg viewBox="0 0 800 390" className="w-full" role="img" aria-describedby="sankey-summary">
        {stages.map((stage, index) => (
          <text key={stage} x={70 + index * 330} y="16" fontSize="11" fontWeight="700" fill="#64748b">
            {stage === 'awareness' ? '認知経路' : stage === 'purpose' ? '来場目的' : '満足度'}
          </text>
        ))}
        <g fill="none" stroke="#1a60a2" strokeOpacity=".16">
          {story.sankey.links.map((link, index) => {
            const source = sankeyPositions.get(link.source);
            const target = sankeyPositions.get(link.target);
            if (!source || !target) return null;
            return (
              <path
                key={`${link.source}-${link.target}-${index}`}
                d={`M${source.x + 78},${source.y} C${source.x + 178},${source.y} ${target.x - 100},${target.y} ${target.x - 8},${target.y}`}
                strokeWidth={Math.max(1, link.value / 24)}
              />
            );
          })}
        </g>
        {story.sankey.nodes.map((node) => {
          const position = sankeyPositions.get(node.id);
          if (!position) return null;
          const height = Math.max(16, Math.min(52, node.value / 23));
          return (
            <g key={node.id} transform={`translate(${position.x},${position.y})`}>
              <rect x="-7" y={-height / 2} width="14" height={height} rx="7" fill="#1a60a2" />
              <text x="14" y="-3" fontSize="13" fontWeight="700" fill="#161616">{node.label}</text>
              <text x="14" y="13" fontSize="10" fill="#64748b">{node.value.toFixed(1)}</text>
            </g>
          );
        })}
      </svg>
      <ul className="sr-only">
        {story.sankey.links.filter((link) => link.value > 0).map((link) => {
          const source = story.sankey.nodes.find((node) => node.id === link.source)?.label ?? link.source;
          const target = story.sankey.nodes.find((node) => node.id === link.target)?.label ?? link.target;
          return <li key={`${link.source}-${link.target}`}>{source}から{target}：{link.value.toFixed(1)}人相当</li>;
        })}
      </ul>
      <p id="sankey-summary" className="mt-5 text-sm leading-relaxed text-atc-slate">
        各段階の合計は1,386人で保存される。認知経路は複数回答のため、一人分を選択肢数で按分している。
      </p>
    </ChartFrame>
  );
}

function geometryPath(coordinates: GeoMultiPolygonCoordinates, bbox: KansaiMapArtifact['bbox']): string {
  const [west, south, east, north] = bbox;
  const width = Math.max(east - west, 0.001);
  const height = Math.max(north - south, 0.001);
  return coordinates.map((polygon) => polygon.map((ring) => ring.map(([longitude, latitude], index) => {
    const x = (longitude - west) / width * 800;
    const y = (north - latitude) / height * 500;
    return `${index === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`;
  }).join(' ') + ' Z').join(' ')).join(' ');
}

function DistanceFallback({ reason, localRate, outsideRate }: { reason: 'loading' | 'error'; localRate: number; outsideRate: number }) {
  return (
    <div>
      {reason === 'error' && (
        <p role="status" className="mb-4 rounded-xl border border-atc-navy/20 bg-atc-surface p-3 text-sm font-bold">
          地図を読み込めないため、同じ比較を距離リングで表示しています。
        </p>
      )}
      <div className="relative mx-auto aspect-[16/8] max-w-2xl" role="img" aria-label="住之江区相当と域外の不満率比較">
        <div className="absolute inset-0 rounded-[50%] border border-atc-primary/15" />
        <div className="absolute inset-[16%] rounded-[50%] border border-atc-primary/30" />
        <div className="absolute inset-[31%] grid place-items-center rounded-[50%] bg-atc-primary text-center text-sm font-bold text-white">
          住之江区相当<br /><strong className="text-2xl">{localRate}%</strong>
        </div>
        <div className="absolute right-[2%] top-[8%] rounded-full bg-white px-3 py-2 text-sm font-bold shadow-atc-card">
          域外 {outsideRate}%
        </div>
      </div>
    </div>
  );
}

export function municipalityNamesMatch(countName: string, mapName: string): boolean {
  return countName === mapName || countName.replace(/^.+郡/, '') === mapName;
}

export function ChapterTwoVisuals({
  story,
  beat,
  map,
  mapError = false,
}: {
  story: Story;
  beat: 0 | 1;
  map?: KansaiMapArtifact | null;
  mapError?: boolean;
}) {
  const h = story.headlines;
  if (beat === 0) {
    return (
      <ChartFrame title="当日の満足度" description="満足・非常に満足を高評価として集計。">
        <div className="grid gap-6 sm:grid-cols-3">
          <DataLabel value={h.verySatisfied.n} label="非常に満足" />
          <DataLabel value={h.satisfied.n} label="満足" />
          <DataLabel value={h.satisfactionAverage.toFixed(2)} label="平均（5点満点）" />
        </div>
        <div className="mt-8 h-6 overflow-hidden rounded-full bg-atc-navy/10">
          <div className="h-full rounded-full bg-atc-primary" style={{ width: `${h.positiveSatisfaction.rate}%` }} />
        </div>
        <p className="mt-3 text-sm font-bold">高評価 {h.positiveSatisfaction.rate}%　初回来館でも {h.firstVisitPositiveRate}%</p>
      </ChartFrame>
    );
  }
  const local = story.geography.localComparison;
  return (
    <ChartFrame title="近隣ほど、不満率が高い" description="郵便番号559を住之江区相当として比較。3.3% 対 0.6%。">
      {map ? (
        <svg viewBox="0 0 800 500" className="mx-auto max-h-[28rem] w-full" role="img" aria-label="近畿6府県の市区町村別来場者数">
          {map.features.map((feature) => {
            const count = story.geography.municipalities.find((item) =>
              municipalityNamesMatch(item.city, feature.properties.municipalityName) &&
              item.pref === feature.properties.prefectureName
            )?.n ?? 0;
            const opacity = count === 0 ? 0.08 : Math.min(0.9, 0.18 + Math.log2(count + 1) / 8);
            return (
              <path
                key={feature.id}
                d={geometryPath(feature.geometry.coordinates, map.bbox)}
                fill="#1a60a2"
                fillOpacity={opacity}
                stroke="#fafbff"
                strokeWidth="0.65"
              />
            );
          })}
        </svg>
      ) : <DistanceFallback reason={mapError ? 'error' : 'loading'} localRate={local.localLowRate} outsideRate={local.outsideLowRate} />}
      {map && (
        <div className="mt-2 flex items-center gap-3 text-xs font-bold text-atc-slate" aria-label="地図の濃さ。薄い色は0人、濃い色ほど来場者が多い">
          <span>0人</span><span className="h-2 flex-1 bg-gradient-to-r from-atc-primary/10 to-atc-primary/90" /><span>多い</span>
        </div>
      )}
      <div className="mt-4 flex flex-wrap gap-3">
        <span className="rounded-full bg-atc-primary px-4 py-2 text-sm font-bold text-white">住之江区相当 {local.localLowRate}%</span>
        <span className="rounded-full border border-atc-navy/15 bg-white px-4 py-2 text-sm font-bold">域外 {local.outsideLowRate}%</span>
      </div>
      <p className="mt-5 text-sm leading-relaxed text-atc-slate">
        住之江区相当は n={local.localN}、あまり満足していない {local.localLowN}件。域外は n={local.outsideN}、{local.outsideLowN}件。
      </p>
    </ChartFrame>
  );
}

export function ChapterThreeVisuals({ story, beat }: { story: Story; beat: 0 | 1 }) {
  const h = story.headlines;
  if (beat === 0) {
    return (
      <ChartFrame title="オフィス認知" description="来館して初めて、ATCがオフィスビルだと知った回答。">
        <p className="text-6xl font-bold text-atc-primary">{h.officeFirst.rate}%</p>
        <p className="mt-3 text-base font-bold">{h.officeFirst.n}人。初回来館者では {h.firstVisitOfficeFirstRate}%。</p>
        <p className="mt-6 text-sm text-atc-slate">主要施設を一つも知らない回答者は {h.facilityNone.n}人・{h.facilityNone.rate}%。</p>
      </ChartFrame>
    );
  }
  const max = Math.max(...story.facilityAwareness.map((item) => item.n));
  return (
    <ChartFrame title="主要施設の認知" description="認知数の上位4施設。">
      <div className="space-y-4">
        {story.facilityAwareness.map((item) => (
          <div key={item.label}>
            <div className="mb-1 flex justify-between gap-4 text-sm font-bold"><span>{item.label}</span><span>{item.n}</span></div>
            <div className="h-3 rounded-full bg-atc-navy/10"><div className="h-full rounded-full bg-atc-primary" style={{ width: `${item.n / max * 100}%` }} /></div>
          </div>
        ))}
      </div>
    </ChartFrame>
  );
}

export function ChapterFourVisuals({ story, beat }: { story: Story; beat: 0 | 1 }) {
  if (beat === 0) {
    return (
      <ChartFrame title="期待される基礎機能" description="五つの最大需要は、既存専門施設との直接対応を置かない。">
        <div className="grid gap-3 sm:grid-cols-5">
          {story.desiredFacilities.map((item) => (
            <div key={item.label} className="rounded-2xl border-2 border-atc-accent bg-orange-50 p-4">
              <strong className="block text-sm">{item.label}</strong>
              <span className="mt-2 block text-3xl font-bold tabular-nums text-atc-ink">{item.n}</span>
            </div>
          ))}
        </div>
      </ChartFrame>
    );
  }
  return (
    <ChartFrame title="需要と既存資産の接続可能性" description="線は回答者の流れや相関ではなく、機能上の接続可能性を表す。">
      <div className="space-y-4">
        {[
          { demand: '体験・学習', assets: ['グリーンエコプラザ', 'IHPC', 'ODP'] },
          { demand: '仕事・勉強', assets: ['IHPC', 'ODP'] },
        ].map((connection) => (
          <div key={connection.demand} className="grid items-center gap-3 md:grid-cols-[12rem_auto_1fr]">
            <div className="rounded-xl bg-atc-navy px-4 py-3 font-bold text-white">{connection.demand}</div>
            <span className="hidden text-2xl text-atc-primary md:block" aria-hidden="true">→</span>
            <div className="flex flex-wrap gap-2">
              {connection.assets.map((asset) => (
                <span key={asset} className="rounded-xl border border-atc-primary/20 bg-atc-surface px-4 py-3 font-bold">{asset}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
      <p className="mt-6 border-l-4 border-atc-accent pl-4 text-sm font-bold">「暑い」20件は、自由記述に現れた注視すべき異常値である。</p>
    </ChartFrame>
  );
}

export function ChapterFiveVisuals({ story, beat }: { story: Story; beat: 0 | 1 }) {
  const [selected, setSelected] = useState(beat === 0 ? 0 : 1);
  const storyId = useId();
  const ferry = CURATED_STORIES[0]!;
  const active = CURATED_STORIES[selected] ?? CURATED_STORIES[1]!;
  const displayed = beat === 0 ? ferry : active;
  return (
    <ChartFrame
      title={beat === 0 ? '海から始まり、家族へ続く記憶' : '三つの記憶'}
      description={`${story.episodes.askedN}件の回答機会から、${story.episodes.usableN}件の有効エピソード、${story.episodes.longN}件の長文を確認。`}
    >
      {beat === 1 && (
        <div className="mb-6 flex gap-2" aria-label="残りの三つの記憶を選ぶ">
          {CURATED_STORIES.slice(1).map((item, index) => (
            <button
              type="button"
              key={item.id}
              aria-controls={storyId}
              aria-pressed={selected === index + 1}
              onClick={() => setSelected(index + 1)}
              className={cn(
                'grid h-11 w-11 place-items-center rounded-full border font-bold',
                selected === index + 1 ? 'border-atc-primary bg-atc-primary text-white' : 'border-atc-navy/20 bg-white',
              )}
            >
              <span aria-hidden="true">{index + 2}</span>
              <span className="sr-only">記憶 {index + 2}</span>
            </button>
          ))}
        </div>
      )}
      <blockquote id={storyId} aria-live="polite" className="whitespace-pre-line text-lg font-medium leading-[2] md:text-xl">
        {displayed.text}
      </blockquote>
      <p className="mt-6 text-sm font-bold text-atc-slate">
        {displayed.age}・{displayed.gender}・{displayed.frequency}
      </p>
    </ChartFrame>
  );
}

export function ChapterSixVisuals({ beat }: { beat: 0 | 1 }) {
  if (beat === 0) {
    return (
      <ChartFrame title="満足を、次の来館理由へ接続する" description="施策一覧ではなく、調査結果から導いた一つの合成フロー。">
        <ol className="grid gap-4 md:grid-cols-3">
          {['来館', '館内回遊', '次の来館理由'].map((label, index) => (
            <li key={label} className="relative rounded-2xl bg-atc-navy p-6 text-center text-xl font-bold text-white">
              <span className="mb-2 block text-xs opacity-60">0{index + 1}</span>{label}
            </li>
          ))}
        </ol>
        <p className="mt-6 text-center text-base font-bold">イベントで人を動かし、館内でATC全体を見せ、次の来館理由を残す。</p>
      </ChartFrame>
    );
  }
  return (
    <div className="rounded-3xl bg-atc-navy p-8 text-white shadow-atc-card md:p-12">
      <p className="text-3xl font-bold leading-snug md:text-5xl">ATCに来る理由が、<br />一日で閉じない設計へ。</p>
      <p className="mt-12 text-sm font-bold tracking-wide text-white/70">作成：経営戦略室 ATC</p>
    </div>
  );
}

/** A distinct middle beat for chapter one: the sample composition, not another Sankey. */
export function AudienceSegmentation({ story }: { story: Story }) {
  const h = story.headlines;
  const items = [
    { label: '家族連れ', n: h.family.n, rate: h.family.rate },
    { label: 'イベント目的', n: h.eventPurpose.n, rate: h.eventPurpose.rate },
    { label: '初回＋年1回', n: h.lightVisitors.n, rate: h.lightVisitors.rate },
    { label: '電車で来館', n: h.train.n, rate: h.train.rate },
  ];
  return <ChartFrame title="会場にいた人の構成" description="イベント当日の来場者像です。ATC全体の来訪者構成そのものではありません。">
    <div className="grid gap-3 sm:grid-cols-2">
      {items.map((item) => <div key={item.label} className="rounded-2xl border border-atc-navy/10 bg-atc-surface p-5">
        <div className="flex items-end justify-between gap-3"><span className="font-bold text-atc-ink">{item.label}</span><strong className="text-2xl tabular-nums text-atc-primary">{item.rate}%</strong></div>
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-atc-navy/10"><span className="block h-full rounded-full bg-atc-primary" style={{ width: `${item.rate}%` }} /></div>
        <p className="mt-2 text-xs font-bold text-atc-slate">{item.n.toLocaleString()}人 / 1,386人</p>
      </div>)}
    </div>
  </ChartFrame>;
}

export function UnderstandingContrast({ story }: { story: Story }) {
  const h = story.headlines;
  return <ChartFrame title="満足と理解は、同じ数字ではない" description="来館当日の高評価と、ATC全体を知っていることは別の設問です。">
    <div className="grid gap-4 md:grid-cols-[1fr_auto_1fr] md:items-stretch">
      <div className="rounded-3xl bg-atc-primary p-6 text-white"><p className="text-sm font-bold text-white/75">当日の満足</p><strong className="mt-8 block text-6xl tabular-nums">{h.positiveSatisfaction.rate}%</strong><p className="mt-4 text-sm">満足・非常に満足<br />{h.positiveSatisfaction.n.toLocaleString()}人 / 1,386人</p></div>
      <div className="grid place-items-center text-4xl font-bold text-atc-slate" aria-hidden="true">≠</div>
      <div className="rounded-3xl border-2 border-atc-accent bg-orange-50 p-6"><p className="text-sm font-bold text-atc-ink">主要施設を一つも知らない</p><strong className="mt-8 block text-6xl tabular-nums text-atc-ink">{h.facilityNone.rate}%</strong><p className="mt-4 text-sm text-atc-slate">{h.facilityNone.n.toLocaleString()}人 / 1,386人</p></div>
    </div>
  </ChartFrame>;
}

export function NegativeReality({ story }: { story: Story }) {
  const items = story.negativeThemes.filter((theme) => ['far', 'empty', 'aging', 'unclear', 'low-vitality', 'heat-ac'].includes(theme.id));
  const max = Math.max(...items.map((item) => item.n));
  return <ChartFrame title="自由記述に残った、来館前と滞在中の現実" description="監査済みテーマ分類。複数テーマを含む回答があるため、合計は回答数になりません。">
    <div className="space-y-4">{items.map((item) => <div key={item.id} className="grid grid-cols-[minmax(8rem,1fr)_minmax(6rem,2fr)_auto] items-center gap-3">
      <span className="text-sm font-bold text-atc-ink">{item.label}</span><span className="h-4 overflow-hidden rounded-full bg-atc-navy/10"><span className="block h-full rounded-full bg-atc-accent" style={{ width: `${item.n / max * 100}%` }} /></span><strong className="tabular-nums text-atc-ink">{item.n}件</strong>
    </div>)}</div>
    <p className="mt-6 border-l-4 border-atc-accent pl-4 text-sm font-bold text-atc-ink">「暑い」20件は厳密な単語カウント。上の「暑さ・空調」46件は、関連表現を含む広義テーマです。</p>
  </ChartFrame>;
}

export function VoicesCollectionBridge({ story }: { story: Story }) {
  return <ChartFrame title="エピソードは、全員の代表ではない" description="回答機会があった人の中から、個人情報を除去して読める断片だけを公開しています。">
    <div className="grid gap-3 sm:grid-cols-3"><DataLabel value={story.episodes.askedN} label="回答機会" /><DataLabel value={story.episodes.usableN} label="有効エピソード" /><DataLabel value={story.episodes.longN} label="長文" /></div>
    <p className="mt-7 text-sm leading-relaxed text-atc-slate">声は数を補強するためではなく、数字だけでは見えない記憶・要望・批判を読み取る入口です。全文原票や個人を特定しうる情報は公開しません。</p>
  </ChartFrame>;
}

export function MethodologyBridge({ story }: { story: Story }) {
  const synthesis = story.evidence.find((item) => item.id === 'synthesis');
  return <ChartFrame title="この調査を、次の来館理由までつなげる" description="以下は回答者が直接選んだ設問ではなく、数値と自由記述を踏まえた編集部の統合です。">
    <ol className="grid gap-3 md:grid-cols-3">{['来館', '館内回遊', '次の来館理由'].map((label, index) => <li key={label} className="rounded-2xl bg-atc-navy p-5 text-center font-bold text-white"><span className="mb-2 block text-xs text-white/60">0{index + 1}</span>{label}</li>)}</ol>
    <p className="mt-6 text-lg font-bold leading-relaxed text-atc-navy">{synthesis?.value}</p>
  </ChartFrame>;
}
